/**
 * BookingDetailPage — operator-facing booking detail view (BON-145).
 *
 * Mounted top-level at `/admin/bookings/:id` (the ops `Booking.id` UUID). Modelled on
 * PromotionDetailPage: useParams → fetch-by-id → back button + two-column body
 * with a right rail. Re-fetches whenever `:id` changes, so the rebooking-chain
 * links (which point at neighbouring `/admin/bookings/:id`) are walkable hop-by-hop.
 *
 * Action gating + the permission-gated cutoff override live in BookingActions;
 * the chain lives in RebookingChain; the presentational cards in
 * BookingDetailCards; the header in BookingDetailHeader; the loaded body
 * (BON-147's Refund wiring included) in LoadedBooking. Split across files to
 * keep `react/no-multi-comp`'s one-component-per-file rule.
 *
 * Built beyond the Confirmed-only mock: Pending-payment / Superseded / Refunded
 * status treatments, the comp treatment, and the rebooking-chain rail are
 * designed consistent with the mock and flagged for design review.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchBookingDetail, type BookingDetail } from '../../api/bookingsV2';

import { LoadedBooking } from './LoadedBooking';

export default function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [booking, setBooking] = useState<BookingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Silent re-read after a refund: the drawer stays open as its own receipt,
  // so this must NOT flip `loading` — that would unmount the drawer along
  // with the rest of the page.
  const reload = useCallback(() => {
    if (!id) return;
    fetchBookingDetail(id)
      .then((b) => setBooking(b))
      .catch(() => {
        // A background refetch failing leaves the last-known booking on
        // screen rather than replacing a working page with an error state.
      });
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    // Reset on id change so a chain hop shows the loading state, not stale data.
    setLoading(true);
    setError('');
    setBooking(null);
    fetchBookingDetail(id)
      .then((b) => {
        if (!cancelled) setBooking(b);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load booking');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-12 text-center text-stone-500 dark:text-stone-400 text-sm">
        Loading…
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-12 text-center">
        <p className="text-red-700 dark:text-red-300 text-sm">{error || 'Booking not found'}</p>
        <button
          onClick={() => navigate('/admin/bookings')}
          className="mt-4 text-sm text-moss-600 dark:text-moss-400 hover:underline"
        >
          Back to bookings
        </button>
      </div>
    );
  }

  return <LoadedBooking booking={booking} reload={reload} />;
}
